import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Menu, X, Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { getAllProducts, Product } from '../utils/productManager';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const headerRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchOverlayRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch products once for search
  useEffect(() => {
    const fetchProducts = async () => {
      const products = await getAllProducts();
      setAllProducts(products);
    };
    fetchProducts();
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(() => {
      const query = searchQuery.toLowerCase().trim();
      const results = allProducts.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          (p.code && p.code.toLowerCase().includes(query))
      );
      setSearchResults(results);
      setIsSearching(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery, allProducts]);

  // Animate search overlay
  useEffect(() => {
    if (!searchOverlayRef.current) return;

    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
      gsap.set(searchOverlayRef.current, { display: 'flex' });
      gsap.to(searchOverlayRef.current, {
        autoAlpha: 1,
        duration: 0.4,
        ease: 'power2.out',
      });
      // Focus input after animation
      setTimeout(() => searchInputRef.current?.focus(), 300);
    } else {
      gsap.to(searchOverlayRef.current, {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: () => {
          if (searchOverlayRef.current) {
            gsap.set(searchOverlayRef.current, { display: 'none' });
          }
          document.body.style.overflow = '';
        },
      });
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isSearchOpen]);

  // Close search on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  // Close search when route changes
  useEffect(() => {
    setIsSearchOpen(false);
    setIsOpen(false);
  }, [location.pathname]);

  useGSAP(() => {
    gsap.fromTo(headerRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.2, ease: 'power4.out', delay: 0.2 }
    );
  }, { scope: headerRef });

  useGSAP(() => {
    if (isOpen) {
      gsap.to(menuRef.current, {
        x: '0%',
        autoAlpha: 1,
        duration: 0.6,
        ease: 'power3.inOut',
      });
    } else {
      gsap.to(menuRef.current, {
        x: '100%',
        autoAlpha: 0,
        duration: 0.6,
        ease: 'power3.inOut',
      });
    }
  }, { dependencies: [isOpen], scope: menuRef });

  const handleNavigation = (path: string, hash?: string) => {
    setIsOpen(false);
    setIsSearchOpen(false);

    if (path === '/') {
      if (hash) {
        if (location.pathname === '/') {
          const element = document.getElementById(hash);
          element?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate({ pathname: '/', hash: hash });
        }
      } else {
        navigate('/');
      }
    } else {
      navigate(path);
    }
  };

  const handleResultClick = (productId: number) => {
    setIsSearchOpen(false);
    navigate(`/products/${productId}`);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] md:max-w-6xl z-[100] px-4 md:px-6 py-2 md:py-3 flex justify-between items-center bg-white/60 backdrop-blur-xl border border-white/50 shadow-2xl rounded-full transition-all duration-300 hover:bg-white/80"
      >
        {/* Left: Logo Icon */}
        <div
          className="flex items-center cursor-pointer group shrink-0 relative z-20"
          onClick={() => handleNavigation('/')}
        >
          <img
            src="https://kwvacrehozikjyxymprc.supabase.co/storage/v1/object/public/product-images/1.png"
            alt="D'mans Symbol"
            className="h-8 md:h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* Center: Logo Name */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10 w-full flex justify-center pointer-events-none md:pointer-events-auto"
          onClick={() => handleNavigation('/')}
        >
          <img
            src="https://kwvacrehozikjyxymprc.supabase.co/storage/v1/object/public/product-images/2.png"
            alt="D'mans Text"
            className="h-4 md:h-6 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
          />
        </div>

        {/* Right: Search + Menu */}
        <div className="flex items-center gap-2 shrink-0 relative z-20">
          {/* Search Trigger */}
          <button
            id="search-trigger"
            onClick={() => setIsSearchOpen(true)}
            className="group p-2 bg-transparent rounded-full hover:bg-diwan-gold/10 transition-all duration-300 focus:outline-none"
            aria-label="Search products"
          >
            <Search size={18} className="text-diwan-gold group-hover:text-diwan-gold transition-colors" />
          </button>

          {/* Menu Trigger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="group flex items-center gap-3 focus:outline-none"
          >
            <span className="hidden md:block uppercase text-[10px] font-bold tracking-[0.2em] text-diwan-cream group-hover:text-diwan-gold transition-colors">
              Menu
            </span>
            <div className="p-2 bg-diwan-gold/10 rounded-full group-hover:bg-diwan-gold group-hover:text-white transition-all duration-300 border border-diwan-gold/20 group-hover:border-diwan-gold">
              <Menu size={18} className="text-diwan-gold group-hover:text-white" />
            </div>
          </button>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════
          SEARCH OVERLAY
      ═══════════════════════════════════════════════ */}
      <div
        ref={searchOverlayRef}
        className="fixed inset-0 z-[120] flex-col items-center"
        style={{ display: 'none', visibility: 'hidden', opacity: 0 }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-2xl"
          onClick={() => setIsSearchOpen(false)}
        />

        {/* Search Content */}
        <div className="relative z-10 w-full max-w-3xl mx-auto px-6 pt-24 md:pt-32 pb-10 flex flex-col h-full">
          {/* Close Button */}
          <button
            onClick={() => setIsSearchOpen(false)}
            className="absolute top-8 right-6 p-3 rounded-full border border-white/10 hover:border-white/40 hover:rotate-90 transition-all duration-300"
          >
            <X size={20} className="text-white/70" />
          </button>

          {/* Search Input */}
          <div className="relative mb-8">
            <div className="flex items-center gap-4 border-b-2 border-white/20 focus-within:border-white/60 transition-colors duration-300 pb-4">
              <Search size={24} className="text-white/40 shrink-0" />
              <input
                ref={searchInputRef}
                id="search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search furniture, materials, categories..."
                className="bg-transparent w-full text-white text-2xl md:text-4xl font-serif placeholder:text-white/20 focus:outline-none caret-white/60"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                  className="text-white/30 hover:text-white/70 transition-colors shrink-0"
                >
                  <X size={20} />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-white/30 text-xs tracking-widest uppercase mt-3 font-sans">
                {isSearching ? 'Searching...' : `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} found`}
              </p>
            )}
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto custom-scrollbar pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}>
            {!searchQuery && (
              <div className="text-center mt-16">
                <p className="text-white/15 text-sm tracking-widest uppercase font-sans">
                  Start typing to explore our collection
                </p>
              </div>
            )}

            {searchQuery && !isSearching && searchResults.length === 0 && (
              <div className="text-center mt-16">
                <p className="text-white/40 text-lg font-serif italic mb-2">No pieces found</p>
                <p className="text-white/20 text-sm font-sans">Try a different search term</p>
              </div>
            )}

            {searchResults.map((product, index) => (
              <button
                key={product.id}
                onClick={() => handleResultClick(product.id)}
                className="w-full flex items-center gap-5 p-4 rounded-2xl hover:bg-white/5 transition-all duration-300 text-left group mb-1"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Product Image */}
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/5 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center p-2 group-hover:border-white/25 transition-colors">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-sans text-sm md:text-base font-semibold tracking-wide uppercase truncate group-hover:text-white/90 transition-colors">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-white/30 text-xs font-sans tracking-widest uppercase">
                      {product.category}
                    </span>
                    {product.code && (
                      <>
                        <span className="text-white/10">·</span>
                        <span className="text-white/20 text-xs font-mono">
                          {product.code}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-white/20 text-xs font-sans mt-1 line-clamp-1 hidden md:block">
                    {product.description}
                  </p>
                </div>

                {/* Arrow */}
                <div className="text-white/10 group-hover:text-white/40 transition-colors shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {/* Bottom Hint */}
          <div className="mt-4 pt-4 border-t border-white/5 text-center">
            <p className="text-white/15 text-[10px] tracking-widest uppercase font-sans">
              Press ESC to close
            </p>
          </div>
        </div>
      </div>

      {/* Full Screen Menu */}
      <div
        ref={menuRef}
        className="fixed inset-0 bg-white z-[110] transform translate-x-full flex items-center justify-center"
      >
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-8 right-8 p-3 border border-diwan-cream/10 rounded-full hover:border-diwan-gold hover:rotate-90 transition-all duration-300"
        >
          <X size={24} className="text-diwan-gold" />
        </button>

        <nav className="flex flex-col gap-8 text-center">
          <button
            onClick={() => handleNavigation('/')}
            className={`font-serif text-4xl md:text-6xl text-diwan-cream hover:text-diwan-gold transition-colors duration-300 italic ${isActive('/') ? 'text-diwan-gold' : ''}`}
          >
            Home
          </button>

          <button
            onClick={() => handleNavigation('/products')}
            className={`font-serif text-4xl md:text-6xl text-diwan-cream hover:text-diwan-gold transition-colors duration-300 italic ${isActive('/products') ? 'text-diwan-gold' : ''}`}
          >
            Our Products
          </button>

          <button
            onClick={() => handleNavigation('/about')}
            className={`font-serif text-4xl md:text-6xl text-diwan-cream hover:text-diwan-gold transition-colors duration-300 italic ${isActive('/about') ? 'text-diwan-gold' : ''}`}
          >
            About Us
          </button>

          <button
            onClick={() => handleNavigation('/', 'philosophy')}
            className="font-serif text-4xl md:text-6xl text-diwan-cream hover:text-diwan-gold transition-colors duration-300 italic"
          >
            Philosophy
          </button>

          <button
            onClick={() => handleNavigation('/services')}
            className={`font-serif text-4xl md:text-6xl text-diwan-cream hover:text-diwan-gold transition-colors duration-300 italic ${isActive('/services') ? 'text-diwan-gold' : ''}`}
          >
            Services
          </button>

          <button
            onClick={() => handleNavigation('/contact')}
            className={`font-serif text-4xl md:text-6xl text-diwan-cream hover:text-diwan-gold transition-colors duration-300 italic ${isActive('/contact') ? 'text-diwan-gold' : ''}`}
          >
            Contact
          </button>
        </nav>

        <div className="absolute bottom-10 left-0 w-full text-center">
          <div className="flex flex-col items-center justify-center gap-4">
            <img
              src="https://kwvacrehozikjyxymprc.supabase.co/storage/v1/object/public/product-images/2.png"
              alt="D'mans Text"
              className="h-6 w-auto object-contain opacity-50"
            />
            <p className="text-diwan-cream/40 text-xs tracking-widest font-sans">Ranbir Singh Dhiman & Sons | Est. 1958</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;